import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Link, navigate } from "gatsby";

import useSearchContext from "@hooks/useSearchContext"; // ✅ named import
import SearchResult from "@components/SearchResult";
import { useSearch } from "@hooks/useSearch";
import { useSearchThumbs } from "@hooks/useSearchThumbs";

const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        {...props}
    >
        <path
            d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-5.5-5.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

// 🔒 Reusable body-scroll lock (SSR-safe)
function useLockBodyScroll(lock: boolean) {
    useEffect(() => {
        if (!lock || typeof window === "undefined") return;

        const body = document.body;
        const docEl = document.documentElement;

        const scrollY = window.scrollY || window.pageYOffset || docEl.scrollTop || 0;

        // snapshot original inline styles
        const original = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
            paddingRight: body.style.paddingRight,
        };

        // prevent layout shift when hiding scrollbar
        const scrollbarWidth = window.innerWidth - docEl.clientWidth;
        if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

        // lock
        body.style.overflow = "hidden";
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.width = "100%";

        return () => {
            // restore
            body.style.overflow = original.overflow;
            body.style.position = original.position;
            body.style.top = original.top;
            body.style.width = original.width;
            body.style.paddingRight = original.paddingRight;

            window.scrollTo(0, scrollY);
        };
    }, [lock]);
}

export const SearchModal: React.FC = () => {
    const { isOpen, close, onNavigateProduct } = useSearchContext();
    const { query, setQuery, results, isSearching } = useSearch();
    const inputRef = useRef<HTMLInputElement>(null);
    const { thumb } = useSearchThumbs();

    // ✅ lock body when open
    useLockBodyScroll(isOpen);

    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [close]);

    const goToProduct = useCallback(
        (slug: string) => {
            close();
            if (onNavigateProduct) onNavigateProduct(slug);
            else navigate(`/product/${slug}/`);
        },
        [close, onNavigateProduct],
    );

    const content = useMemo(
        () => (
            <div
                className="fixed inset-0 z-[100] flex items-start justify-center overscroll-contain"
                role="dialog"
                aria-modal="true"
            >
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/50" onClick={close} />

                <div className="relative mt-24 w-full max-w-2xl rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-black/10 dark:bg-neutral-900 dark:text-white">
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800">
                            <SearchIcon className="h-5 w-5 opacity-70" />
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search products..."
                                className="w-full bg-transparent outline-none placeholder:text-neutral-400"
                                autoComplete="off"
                            />
                            {query ? (
                                <button
                                    onClick={() => setQuery("")}
                                    className="text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-white"
                                >
                                    clear
                                </button>
                            ) : null}
                        </div>
                        <Link
                            to="/search"
                            onClick={close}
                            className="text-center text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-white md:hidden"
                        >
                            Visit search page for better results
                        </Link>
                    </div>

                    {/* Results */}
                    <div className="mt-3 max-h-[60vh] overflow-auto overscroll-contain">
                        {isSearching ? (
                            <div className="p-6 text-center text-sm text-neutral-500">
                                Searching…
                            </div>
                        ) : results.length ? (
                            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                                {results.slice(0, 30).map((r) => {
                                    const imgUrl = thumb(r.image as string | undefined, {
                                        width: 96,
                                    });
                                    return (
                                        <li
                                            key={r.id}
                                            className="group cursor-pointer px-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                                        >
                                            <SearchResult
                                                onClick={() => goToProduct(r.slug)}
                                                imgUrl={imgUrl}
                                                {...r}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : query ? (
                            <div className="p-6 text-center text-sm text-neutral-500">
                                No matches. Try another keyword.
                            </div>
                        ) : (
                            <div className="p-6 text-center text-sm text-neutral-500">
                                Type to search products by title, category, tags, description, or
                                meta keywords.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        ),
        [close, goToProduct, isSearching, query, results, setQuery, thumb], // ✅ include thumb
    );

    if (!isOpen) return null;
    return content;
};

// —— Highlight helpers (safe & fast)
function highlight(text: string, rawTokens?: string[]) {
    if (!text) return text;
    const tokens = (rawTokens || []).map((t) => (t || "").trim()).filter(Boolean);
    if (!tokens.length) return text;

    const uniq = Array.from(new Set(tokens.map((t) => t.toLowerCase()))).sort(
        (a, b) => b.length - a.length,
    );
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(${uniq.map(esc).join("|")})`, "gi");

    const parts = text.split(re);
    if (parts.length === 1) return text;

    let key = 0;
    return (
        <>
            {parts.map((p, i) => {
                if (i % 2 === 1) {
                    return (
                        <mark key={`m-${key++}`} className="rounded bg-yellow-200/70 px-0.5">
                            {p}
                        </mark>
                    );
                }
                return <span key={`t-${key++}`}>{p}</span>;
            })}
        </>
    );
}

function splitAndMark(text: string, token: string) {
    /* … */
}
